import { usePortalStateStore, useTokenStore, useUserStore } from '@/plugins/stores';
import { Router } from 'vue-router';
import { Forms } from '@/enums/Forms';
import AuthController from '@/controllers/AuthController';
import TokenResponse from '@/models/TokenResponse';
import NavigationHelper from '@/util/NavigationHelper';
import UserInfoController from '@/controllers/UserInfoController';
import { Address } from '@/models/Address';
import Response from '@/models/Response';
import { AuthResponseStatus } from '@/enums/AuthResponseStatus';
import UserInfo from '@/models/UserInfo';
import Service from '@/models/Service';
import NotificationMessage from '@/models/NotificationMessage';
import UserInfoResponse from '@/models/UserInfoResponse';
import Settings from '@/config/Settings';

export default class AuthenticationHelper {
  public static async useAuthenticationCode(router: Router, code: string) {
    if (code && code !== '') {
      const tokenResponse = await AuthController.getTokens(code);
      if (tokenResponse instanceof TokenResponse) {
        AuthenticationHelper.setTokens(tokenResponse);
        await AuthenticationHelper.getUserInfo();
        if (router.currentRoute.value.name) {
          const currentRouteName = router.currentRoute.value.name.toString();
          if (currentRouteName === Forms.SIGN_IN.toString() || currentRouteName === Forms.ACTIVATE.toString()) {
            await NavigationHelper.openForm(router, Forms.SIGNED_IN);
          } else {
            await NavigationHelper.openForm(router, router.currentRoute.value.name.toString());
          }
        }
      }
    }
  }

  public static async cookieSignIn(router: Router, queryString = '') {
    if (AuthenticationHelper.doesHttpOnlyCookieExist('polteq-id')) {
      if (queryString !== '') {
        await AuthController.cookieSignIn(queryString);
      } else {
        const matchedRoute = router.resolve(window.location.search);
        if (matchedRoute.name === Forms.ACTIVATE) {
          await this.logout(router, false);
          const activationCode = new URLSearchParams(window.location.search).get('activationCode');
          const queryParams = { activationCode };
          await NavigationHelper.openForm(router, matchedRoute.name, queryParams);
        } else if (matchedRoute.name === Forms.RESET_PASSWORD) {
          await this.logout(router, false);
          const resetCode = new URLSearchParams(window.location.search).get('resetCode');
          const queryParams = { resetCode };
          await NavigationHelper.openForm(router, matchedRoute.name, queryParams);
        } else if (matchedRoute.name === Forms.FORGOT_PASSWORD) {
          await this.logout(router, false);
        } else if (matchedRoute.name && matchedRoute.name !== Forms.SIGN_IN) {
          await NavigationHelper.openForm(router, matchedRoute.name.toString());
        } else {
          await NavigationHelper.openForm(router, Forms.SIGNED_IN);
        }
      }
      return true;
    }
    return false;
  }

  public static doesHttpOnlyCookieExist(cookiename: string) {
    const date = new Date();
    date.setTime(date.getTime() + 1000);
    const expires = 'expires=' + date.toUTCString();
    document.cookie = cookiename + '=new_value;domain=' + Settings.domainName + ';path=/;' + expires;
    return document.cookie.indexOf(cookiename + '=') == -1;
  }

  public static async getUserInfo() {
    const authStore = useTokenStore();
    const userStore = useUserStore();
    const userInfoResponse = await UserInfoController.getUserInfo(authStore.accessToken);
    if (userInfoResponse.status === AuthResponseStatus.SUCCESS) {
      AuthenticationHelper.setTokens(userInfoResponse);
      userStore.userInfo = (userInfoResponse as UserInfoResponse).userinfo;
      if (userStore.userInfo.address === null || userStore.userInfo.address === undefined) {
        userStore.userInfo.address = new Address();
      }
    }
  }

  public static setTokens(response: TokenResponse | Response) {
    const store = useTokenStore();
    store.$patch({ accessToken: response.accessToken });
    if (response instanceof TokenResponse) {
      store.$patch({ idToken: response.idToken });
    }
  }

  public static async logout(router: Router, redirect = true) {
    const tokenStore = useTokenStore();
    tokenStore.accessToken = '';
    tokenStore.idToken = '';
    const userStore = useUserStore();
    userStore.userInfo = new UserInfo();
    userStore.services = new Array<Service>();
    const portalStateStore = usePortalStateStore();
    portalStateStore.notificationMessages = new Array<NotificationMessage>();
    await AuthController.logout();
    localStorage.clear();
    if (redirect) {
      await NavigationHelper.openForm(router, Forms.SIGN_IN);
    }
  }
}
